import { Box } from '@mui/material';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadMediaAsset } from '@/hooks/useUploadMediaAsset';
import { useEffectiveIsLocked, useImportView, useNodeData } from '@/components/Recipe/FlowContext';
import { useWorkflowStore } from '@/state/workflow.state';
import { useAppContextMenu } from '@/UI/AppContextMenu/useAppContextMenu';
import { useNodeSetAsCover } from '@/components/Nodes/hooks/useNodeSetAsCover';
import { FlexCol } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { GridViewer } from '@/components/GridViewer/GridViewer';
import { I18N_KEYS } from '@/language/keys';
import { ImportData, FileSortOrder } from '@/types/node';
import { FileKind } from '@/designer/designer';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import GalleryGradientOverlay from '@/components/Common/GalleryGradientOverlay';
import { FilesViewOverlay } from '@/components/Nodes/ModelComponents/FilesViewOverlay/FilesViewOverlay';
import { useIsHovered } from '@/hooks/useIsHovered';
import { UploadProgressOverlay } from '../../Common/FileUploader/UploadProgressOverlay';
import { UploadAnotherOverlay } from '../../Common/FileUploader/UploadAnotherOverlay';
import { FileViewer } from '../Shared/FileViewer';
import { getAssetsTypeTranslationKey, getFileViewerControlsOffset } from '../utils/nodeViewUtils';
import type { MenuAction } from '@/components/Menu/Actions';
import type { MediaAsset } from '@/types/api/assets';

export const ImportNodeViewer = ({
  id,
  files: originalFiles,
  selectedIndex,
  onUpload,
  viewMode = NodeViewMode.Single,
  selectedViewMode,
  onDownloadCurrent,
  onDownloadAll,
  sortOrder,
  onSortOrderChange,
}: {
  id: string;
  files: FileKind[];
  selectedIndex: number;
  onUpload: (file: Partial<MediaAsset> | null) => void;
  viewMode?: NodeViewMode;
  selectedViewMode: NodeViewMode;
  onDownloadCurrent?: () => Promise<void>;
  onDownloadAll?: () => Promise<void>;
  sortOrder?: FileSortOrder;
  onSortOrderChange?: (sortOrder: FileSortOrder) => void;
}) => {
  const files = useMemo<Array<FileKind & { globalIndex: number }>>(() => {
    return originalFiles.map((file, index) => ({
      ...file,
      globalIndex: index,
    }));
  }, [originalFiles]);
  const { t } = useTranslation();
  const role = useWorkflowStore((state) => state.workflowRole);
  const isLocked = useEffectiveIsLocked(id);
  const { canSetPoster, generateNodePreviewImageAndSetPoster } = useNodeSetAsCover(id, 'import');
  const importView = useImportView(id);
  const selectedFile = files[selectedIndex];
  const fileType = selectedFile?.type;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const nodeData = useNodeData<ImportData>(id);
  const { isHovered, ...hoverElementProps } = useIsHovered();

  const handleSetViewMode = useCallback(
    (viewMode: NodeViewMode) => {
      importView.setViewMode(viewMode);
    },
    [importView],
  );

  const { dropzoneState, isInProgress, getAcceptedFileTypesToUpload } = useUploadMediaAsset({
    onUpload,
    allowMultiple: true,
    acceptedFileType: fileType,
  });

  const getAddMoreTextBasedOnCurrentFile = useCallback(() => {
    switch (selectedFile?.type) {
      case 'image':
        return t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.ADD_MORE_IMAGES);
      case 'video':
        return t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.ADD_MORE_VIDEOS);
      case 'audio':
        return t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.ADD_MORE_AUDIO);
      case '3D':
        return t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.ADD_MORE_3D_FILES);
      default:
        return t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.ADD_MORE_FILES);
    }
  }, [selectedFile, t]);

  const handleBack = useCallback(() => {
    const steps = files.length;
    const newIndex = selectedIndex === 0 ? steps - 1 : selectedIndex - 1;
    importView.setSelectedIndex(newIndex, false);
  }, [importView, files, selectedIndex]);

  const handleNext = useCallback(() => {
    const steps = files.length;
    const newIndex = selectedIndex === steps - 1 ? 0 : selectedIndex + 1;
    importView.setSelectedIndex(newIndex, false);
  }, [importView, files, selectedIndex]);

  const handleAddMoreImages = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handle3DLockStateChange = useCallback(
    (locked: boolean) => {
      importView.setCameraLocked(locked);
    },
    [importView],
  );

  const handleFileSelect = useCallback(
    (index: number) => {
      importView.setSelectedIndex(index, false);
    },
    [importView],
  );

  const steps = files.length;
  const fileViewerControlsOffset = getFileViewerControlsOffset(viewMode, selectedFile);

  const coverContextMenuItems = useMemo<MenuAction[]>(
    () => [
      {
        name: t(I18N_KEYS.RECIPE_MAIN.NODES.PREVIEW_NODE.SET_AS_COVER),
        action: () => void generateNodePreviewImageAndSetPoster(),
      },
    ],
    [generateNodePreviewImageAndSetPoster, t],
  );
  const hasCoverContextMenu = !isLocked && role === 'editor' && canSetPoster;
  const { handleContextMenuOpen, contextMenu } = useAppContextMenu({
    items: coverContextMenuItems,
  });
  const fileViewerContextMenu = hasCoverContextMenu ? handleContextMenuOpen : undefined;

  return (
    <FlexCol sx={{ width: '100%', height: '100%', gap: 2 }}>
      <Box
        className="import-node-content"
        sx={{
          width: '100%',
          height: '100%',
          cursor: 'default',
          position: 'relative',
        }}
        {...(role === 'editor' ? dropzoneState.getRootProps() : {})}
      >
        {viewMode === NodeViewMode.Single ? (
          <Box {...hoverElementProps}>
            <FileViewer id={id} index={selectedIndex} asset={selectedFile} handleContextMenu={fileViewerContextMenu} />
            {contextMenu}
            <GalleryGradientOverlay isSelected={isHovered} bottomOffset={fileViewerControlsOffset}>
              <FilesViewOverlay
                asset={selectedFile}
                viewMode={viewMode}
                steps={steps}
                selected={selectedIndex}
                handleBack={handleBack}
                handleNext={handleNext}
                is3DLocked={nodeData.cameraLocked ?? false}
                setIs3DLocked={handle3DLockStateChange}
                setViewMode={handleSetViewMode}
                selectedViewMode={selectedViewMode}
                onDownloadAll={onDownloadCurrent ? () => void onDownloadCurrent() : undefined}
                sortOrder={sortOrder}
                onSortOrderChange={onSortOrderChange}
              />
            </GalleryGradientOverlay>
            {isInProgress ? <UploadProgressOverlay /> : null}
            {dropzoneState.isDragActive ? (
              <UploadAnotherOverlay
                show={dropzoneState.isDragActive}
                sx={{ bottom: fileViewerControlsOffset ?? 0, borderRadius: '7px' }}
              />
            ) : null}
          </Box>
        ) : (
          <Box {...hoverElementProps}>
            <GridViewer nodeId={id} files={files} selectedIndex={selectedIndex} onFileSelect={handleFileSelect} />
            <GalleryGradientOverlay isSelected={isHovered}>
              <FilesViewOverlay
                onDownloadAll={onDownloadAll ? () => void onDownloadAll() : undefined}
                viewMode={viewMode}
                setViewMode={handleSetViewMode}
                selectedViewMode={selectedViewMode}
                sortOrder={sortOrder}
                onSortOrderChange={onSortOrderChange}
                filesNavigationComponent={
                  <Box sx={{ p: 1 }}>
                    {files.length} {t(getAssetsTypeTranslationKey(files))}
                  </Box>
                }
              />
            </GalleryGradientOverlay>
          </Box>
        )}
      </Box>
      {role === 'editor' && (
        <>
          <ButtonContained
            mode="text"
            size="small"
            onClick={handleAddMoreImages}
            disabled={isInProgress}
            sx={{ alignSelf: 'flex-start' }}
          >
            + {getAddMoreTextBasedOnCurrentFile()}
          </ButtonContained>
          <input
            type="file"
            style={{ display: 'none' }}
            {...dropzoneState.getInputProps()}
            ref={fileInputRef}
            accept={getAcceptedFileTypesToUpload()}
          />
        </>
      )}
    </FlexCol>
  );
};
